// src/utils/analytics.js
// One place that fires the same commerce events to BOTH Meta Pixel (fbq) and
// Google Analytics 4 (gtag). Both base tags load from index.html; every call
// here no-ops safely if a tag is blocked, not yet ready, or absent.
//
// Note on page views: GA4 Enhanced Measurement already tracks SPA route
// changes, so we only fire the Meta PageView on navigation (see trackPageView).

const fbq  = (...a) => { try { if (typeof window !== 'undefined' && window.fbq)  window.fbq(...a);  } catch { /* ignore */ } };
const gtag = (...a) => { try { if (typeof window !== 'undefined' && window.gtag) window.gtag(...a); } catch { /* ignore */ } };

const num = (v) => { const n = typeof v === 'number' ? v : Number(v); return Number.isFinite(n) ? n : 0; };

// Meta-only SPA page view (GA4 handles this itself via Enhanced Measurement).
export function trackPageView() {
  fbq('track', 'PageView');
}

// Product detail viewed.
export function trackViewContent(product) {
  if (!product || !product._id) return;
  const value = num(product.price);
  fbq('track', 'ViewContent', {
    content_ids: [product._id],
    content_name: product.name,
    content_type: 'product',
    content_category: product.category,
    value,
    currency: 'INR',
  });
  gtag('event', 'view_item', {
    currency: 'INR',
    value,
    items: [{ item_id: product._id, item_name: product.name, item_brand: product.brand, item_category: product.category, price: value }],
  });
}

// Added to cart.
export function trackAddToCart(product, qty = 1) {
  if (!product || !product._id) return;
  const q = num(qty) || 1;
  const value = num(product.price) * q;
  fbq('track', 'AddToCart', {
    content_ids: [product._id],
    content_name: product.name,
    content_type: 'product',
    value,
    currency: 'INR',
  });
  gtag('event', 'add_to_cart', {
    currency: 'INR',
    value,
    items: [{ item_id: product._id, item_name: product.name, item_brand: product.brand, price: num(product.price), quantity: q }],
  });
}

// Checkout started. `items` are cart lines ({ product, name, price, qty }).
export function trackInitiateCheckout(items = [], value) {
  const ids = items.map((i) => i.product).filter(Boolean);
  fbq('track', 'InitiateCheckout', {
    content_ids: ids,
    content_type: 'product',
    num_items: items.reduce((s, i) => s + (num(i.qty) || 1), 0),
    value: num(value),
    currency: 'INR',
  });
  gtag('event', 'begin_checkout', {
    currency: 'INR',
    value: num(value),
    items: items.map((i) => ({ item_id: i.product, item_name: i.name, price: num(i.price), quantity: num(i.qty) })),
  });
}

// Order placed. Deduped per order (survives a page refresh within the session).
export function trackPurchase(order) {
  if (!order || (!order._id && !order.orderId)) return;
  const oid = order.orderId || order._id;
  const key = 'ke_purch_' + oid;
  try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1'); } catch { /* ignore */ }
  const value = num(order.totalAmount);
  const items = order.items || [];
  fbq('track', 'Purchase', {
    content_ids: items.map((i) => i.product || i._id).filter(Boolean),
    content_type: 'product',
    num_items: items.reduce((s, i) => s + (num(i.qty) || 1), 0),
    value,
    currency: 'INR',
  });
  gtag('event', 'purchase', {
    transaction_id: oid,
    currency: 'INR',
    value,
    items: items.map((i) => ({ item_id: i.product || i._id, item_name: i.name, price: num(i.price), quantity: num(i.qty) })),
  });
}
